import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { UserPlus, Upload, Check, ArrowRight, ArrowLeft } from "lucide-react";
import { PlatformLayout } from "./PlatformLayout";
import { Brand } from "./Brand";
import { authApi, useAuth } from "./authApi";
import { API } from "./api";
import { AgentPresenter } from "./AIAssist";
import { PROFILE_TYPES } from "./profiles";
import { SUPPORTED, LANG_NAMES } from "../i18n";

const TYPES = PROFILE_TYPES;
const inp = "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#C19A6B] transition-colors";

export default function MemberRegister() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [step, setStep] = useState(0);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [f, setF] = useState({
    account_type: "", account_type_other: "", first_name: "", last_name: "", email: "", password: "",
    phone: "", address: "", company: "", city: "", sector: "", website: "", siret: "", pro_card: "",
    description: "", vision: "", off_market_presentation: "", logo: "", photo: "", gallery: [],
    socials: {}, network_mode: "national", ref: sp.get("ref") || "", preferred_lang: "fr",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (file, key) => {
    const fd = new FormData(); fd.append("file", file);
    const res = await fetch(`${API}/public/upload`, { method: "POST", body: fd });
    const d = await res.json();
    if (key === "gallery") set("gallery", [...f.gallery, d.url]); else set(key, d.url);
  };

  const uploadGalleryMulti = async (files) => {
    const list = Array.from(files || []);
    const room = 10 - f.gallery.length;
    if (room <= 0) return;
    const toAdd = list.slice(0, room);
    const urls = await Promise.all(toAdd.map(async (file) => {
      const fd = new FormData(); fd.append("file", file);
      const res = await fetch(`${API}/public/upload`, { method: "POST", body: fd });
      const d = await res.json();
      return d.url;
    }));
    setF((p) => ({ ...p, gallery: [...p.gallery, ...urls].slice(0, 10) }));
  };

  const submit = async () => {
    setErr(""); setLoading(true);
    try {
      const r = await authApi.register(f);
      setUser(r.user);
      setDone(true);
    } catch (e) { setErr(e.message || "Erreur lors de l'inscription."); }
    setLoading(false);
  };

  const canNext = () => {
    if (step === 0) return !!f.account_type && (f.account_type !== "Autre" || f.account_type_other);
    if (step === 1) return f.first_name && f.last_name && f.email && f.password.length >= 6;
    return true;
  };

  const googleSignup = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/mon-compte";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  if (done) {
    return (
      <PlatformLayout searchDock={false} solidHeader>
        <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6 pt-28 pb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md text-center space-y-5" data-testid="register-success">
            <div className="w-14 h-14 mx-auto rounded-full bg-green-500/15 flex items-center justify-center"><Check className="w-7 h-7 text-green-400" /></div>
            <h1 className="text-2xl font-light text-white">Demande envoyée !</h1>
            <p className="text-zinc-400 font-light">Votre demande d'affiliation à <Brand /> a bien été transmise à Vincent Marette. Votre profil sera public dès validation. Vous recevrez une confirmation par e-mail.</p>
            <Link to="/mon-compte" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">Accéder à mon espace <ArrowRight className="w-4 h-4" /></Link>
          </motion.div>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout searchDock={false} solidHeader>
      <div className="min-h-screen bg-[#050505] pt-28 pb-20 px-6">
        <div className="max-w-2xl mx-auto" data-testid="member-register">
          <div className="text-center space-y-2 mb-8">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#C19A6B]/15 flex items-center justify-center"><UserPlus className="w-5 h-5 text-[#C19A6B]" /></div>
            <h1 className="text-3xl font-light text-white">Demander à rejoindre le réseau Off Markette</h1>
            <p className="text-sm text-zinc-400">Créez votre espace conseiller et votre vitrine.</p>
          </div>

          <div className="max-w-sm mx-auto mb-8 space-y-3">
            <button onClick={googleSignup} data-testid="register-google" className="w-full flex items-center justify-center gap-3 bg-white text-[#1a1a1a] font-medium text-sm py-3 hover:bg-zinc-200 transition-colors">
              <img src="https://www.google.com/favicon.ico" alt="" className="w-4 h-4" /> S'inscrire avec Google
            </button>
            <div className="flex items-center gap-3 text-xs text-zinc-500"><div className="flex-1 h-px bg-white/10" /> ou par e-mail <div className="flex-1 h-px bg-white/10" /></div>
          </div>

          <div className="flex items-center justify-center gap-2 mb-8" data-testid="register-steps">
            {["Profil", "Compte", "Vitrine"].map((s, i) => (
              <div key={s} className={`flex items-center gap-2 text-xs uppercase tracking-wider ${i === step ? "text-[#C19A6B]" : "text-zinc-600"}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center border ${i <= step ? "border-[#C19A6B] text-[#C19A6B]" : "border-zinc-700"}`}>{i + 1}</span>{s}{i < 2 && <span className="w-6 h-px bg-white/10 mx-1" />}
              </div>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="space-y-4">
              {step === 0 && (
                <div className="grid sm:grid-cols-2 gap-3">
                  {TYPES.map((t) => (
                    <button key={t} onClick={() => set("account_type", t)} data-testid={`type-${t}`} className={`text-left p-4 border transition-colors ${f.account_type === t ? "border-[#C19A6B] bg-[#C19A6B]/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>{t}</button>
                  ))}
                  {f.account_type === "Autre" && <input className={`${inp} sm:col-span-2`} placeholder="Précisez votre profil" value={f.account_type_other} onChange={(e) => set("account_type_other", e.target.value)} data-testid="type-other-input" />}
                </div>
              )}
              {step === 1 && (
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className={inp} placeholder="Prénom *" value={f.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="reg-first" />
                  <input className={inp} placeholder="Nom *" value={f.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="reg-last" />
                  <input className={inp} type="email" placeholder="E-mail *" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" />
                  <input className={inp} type="password" placeholder="Mot de passe * (6+ car.)" value={f.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" />
                  <input className={inp} placeholder="Téléphone" value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="reg-phone" />
                  <input className={inp} placeholder="Adresse" value={f.address} onChange={(e) => set("address", e.target.value)} data-testid="reg-address" />
                  <div className="sm:col-span-2">
                    <label className="block text-[11px] uppercase tracking-wider text-[#C19A6B] mb-1.5">Langue de mes e-mails</label>
                    <select className={inp} value={f.preferred_lang} onChange={(e) => set("preferred_lang", e.target.value)} data-testid="reg-preferred-lang">
                      {SUPPORTED.map((l) => <option key={l} value={l} className="bg-[#0d0d10]">{LANG_NAMES[l]}</option>)}
                    </select>
                    <p className="text-[10px] text-zinc-500 mt-1">Les e-mails automatiques d'OFF MARKETTE vous seront envoyés dans cette langue.</p>
                  </div>
                </div>
              )}
              {step === 2 && (
                <div className="space-y-3">
                  <div className="grid sm:grid-cols-2 gap-3">
                    <input className={inp} placeholder="Société" value={f.company} onChange={(e) => set("company", e.target.value)} data-testid="reg-company" />
                    <input className={inp} placeholder="Ville principale" value={f.city} onChange={(e) => set("city", e.target.value)} data-testid="reg-city" />
                    <input className={inp} placeholder="Secteur d'activité" value={f.sector} onChange={(e) => set("sector", e.target.value)} />
                    <input className={inp} placeholder="Site internet" value={f.website} onChange={(e) => set("website", e.target.value)} />
                    <input className={inp} placeholder="N° SIRET" value={f.siret} onChange={(e) => set("siret", e.target.value)} />
                    <input className={inp} placeholder="Carte professionnelle" value={f.pro_card} onChange={(e) => set("pro_card", e.target.value)} />
                  </div>
                  <AgentPresenter
                    context={{ name: `${f.first_name} ${f.last_name}`.trim(), company: f.company, city: f.city, sector: f.sector }}
                    description={f.description}
                    vision={f.vision}
                    onGenerated={({ description, vision }) => { set("description", description); set("vision", vision); }}
                  />
                  <textarea className={`${inp} min-h-[80px]`} placeholder="Description / présentation" value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="reg-description" />
                  <textarea className={`${inp} min-h-[70px]`} placeholder="Votre vision du métier" value={f.vision} onChange={(e) => set("vision", e.target.value)} data-testid="reg-vision" />
                  <div className="grid sm:grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 border border-dashed border-white/20 px-4 py-3 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="upload-logo">
                      <Upload className="w-4 h-4 text-[#C19A6B]" /> {f.logo ? "Logo ajouté ✓" : "Logo société"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "logo")} />
                    </label>
                    <label className="flex items-center gap-2 border border-dashed border-white/20 px-4 py-3 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="upload-photo">
                      <Upload className="w-4 h-4 text-[#C19A6B]" /> {f.photo ? "Photo ajoutée ✓" : "Photo de profil"}
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], "photo")} />
                    </label>
                  </div>
                  <label className="flex items-center gap-2 border border-dashed border-white/20 px-4 py-3 cursor-pointer text-sm text-zinc-300 hover:border-[#C19A6B]/50 transition-colors" data-testid="upload-gallery">
                    <Upload className="w-4 h-4 text-[#C19A6B]" /> Galerie ({f.gallery.length}/10 photo{f.gallery.length > 1 ? "s" : ""}) — plusieurs à la fois
                    <input type="file" accept="image/*" multiple className="hidden" disabled={f.gallery.length >= 10} onChange={(e) => e.target.files.length && uploadGalleryMulti(e.target.files)} />
                  </label>
                  <div className="border border-white/10 p-4 space-y-2" data-testid="reg-network">
                    <p className="text-xs uppercase tracking-wider text-[#C19A6B]">Partage dans le réseau</p>
                    <p className="text-[11px] text-zinc-500">Comment souhaitez-vous participer ? (modifiable à tout moment dans « Mon réseau »)</p>
                    <div className="grid sm:grid-cols-3 gap-2">
                      {[{ k: "national", l: "Réseau national", d: "Partage réciproque avec tout le réseau." }, { k: "private_network", l: "Réseau privé", d: "Uniquement mes partenaires choisis." }, { k: "private", l: "Réseau personnel", d: "Seulement mes annonces." }].map((o) => (
                        <button key={o.k} type="button" onClick={() => set("network_mode", o.k)} data-testid={`reg-net-${o.k}`} className={`text-left p-3 border transition-colors ${f.network_mode === o.k ? "border-[#C19A6B] bg-[#C19A6B]/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/30"}`}>
                          <div className="text-xs font-medium">{o.l}</div>
                          <div className="text-[10px] text-zinc-500 mt-0.5 leading-snug">{o.d}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {err && <p className="text-sm text-red-400 mt-4" data-testid="register-error">{err}</p>}

          <div className="flex items-center justify-between mt-8">
            {step > 0 ? <button onClick={() => setStep(step - 1)} className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /> Retour</button> : <span />}
            {step < 2 ? (
              <button onClick={() => canNext() && setStep(step + 1)} disabled={!canNext()} data-testid="register-next" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-40 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">Continuer <ArrowRight className="w-4 h-4" /></button>
            ) : (
              <button onClick={submit} disabled={loading} data-testid="register-submit" className="inline-flex items-center gap-2 bg-[#C19A6B] hover:bg-[#d1af84] disabled:opacity-60 text-white font-medium uppercase tracking-wider text-sm px-6 py-3 transition-colors">{loading ? "Envoi…" : "Envoyer ma demande"} <Check className="w-4 h-4" /></button>
            )}
          </div>
          <p className="text-center text-sm text-zinc-500 mt-6">Déjà membre ? <Link to="/connexion" className="text-[#C19A6B] hover:underline">Se connecter</Link></p>
        </div>
      </div>
    </PlatformLayout>
  );
}
